/**
 * ZITELOU - Subscriptions Page
 * Liste et gestion des abonnements
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PauseCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  Card,
  Input,
  Select,
  Button,
  Table,
  Pagination,
  Badge,
  Loader,
  ConfirmModal,
} from '../components/Common';
import { useApi } from '../hooks';
import api from '../services/api';
import { formatDate, formatCurrency } from '../utils';
import { toast } from 'react-toastify';

const Subscriptions = () => {
    const { loading, execute } = useApi();
    const [subscriptions, setSubscriptions] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 25,
        total: 0,
        totalPages: 0,
    });
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSuspendModal, setShowSuspendModal] = useState(false);
    const [selectedSubscription, setSelectedSubscription] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');

    const fetchSubscriptions = useCallback(async () => {
        await execute(async () => {
            const result = await api.getSubscriptions({
                page: pagination.currentPage,
                limit: pagination.pageSize,
                search: searchTerm,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                type: typeFilter !== 'all' ? typeFilter : undefined,
            });
            setSubscriptions(result.data);
            setPagination(prev => ({
                ...prev,
                total: result.pagination.total,
                totalPages: result.pagination.totalPages,
            }));
        });
    }, [execute, pagination.currentPage, pagination.pageSize, searchTerm, statusFilter, typeFilter]);

    useEffect(() => {
        fetchSubscriptions();
    }, [fetchSubscriptions]);

    const handlePageChange = (newPage) => {
        setPagination(prev => ({ ...prev, currentPage: newPage }));
    };

    const handlePageSizeChange = (newSize) => {
        setPagination(prev => ({
            ...prev,
            pageSize: newSize,
            currentPage: 1
        }));
    };

    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const resetFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setTypeFilter('all');
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    };

    const handleSuspend = async () => {
        try {
            await api.suspendSubscription(selectedSubscription.id);
            toast.success('Abonnement suspendu');
            setShowSuspendModal(false);
            setSelectedSubscription(null);
            fetchSubscriptions();
        } catch (error) {
            toast.error('Erreur lors de la suspension');
        }
    };

    const handleCancel = async () => {
        try {
            await api.cancelSubscription(selectedSubscription.id);
            toast.success('Abonnement résilié');
            setShowCancelModal(false);
            setSelectedSubscription(null);
            fetchSubscriptions();
        } catch (error) {
            toast.error('Erreur lors de la résiliation');
        }
    };

    // Statistics cards - calculated from current page data
    const stats = {
        total: pagination.total,
        active: subscriptions.filter((s) => s.status === 'active').length,
        suspended: subscriptions.filter((s) => s.status === 'suspended').length,
        cancelled: subscriptions.filter((s) => s.status === 'cancelled').length,
    };

    // Calculate display indices
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize + 1;
    const endIndex = Math.min(pagination.currentPage * pagination.pageSize, pagination.total);

    // Count active filters
    const hasActiveFilters = [
        statusFilter !== 'all' ? 1 : 0,
        typeFilter !== 'all' ? 1 : 0,
        searchTerm ? 1 : 0,
    ].reduce((a, b) => a + b, 0);

    // Table columns
    const columns = [
        {
            key: 'stripe_id',
            label: 'ID Stripe',
            render: (value) => (
                <span className="font-mono text-xs text-gray-600">
          {value ? `${value.slice(0, 20)}...` : 'N/A'}
        </span>
            ),
        },
        {
            key: 'user_name',
            label: 'Parent',
            sortable: true,
            render: (value, row) => (
                <div>
                    <p className="font-medium text-gray-900">{value}</p>
                    <p className="text-xs text-gray-500">
                        {row.type === 'monthly' ? 'Mensuel' : 'Annuel'}
                    </p>
                </div>
            ),
        },
        {
            key: 'amount',
            label: 'Montant',
            sortable: true,
            render: (value, row) => (
                <div>
                    <p className="font-semibold text-gray-900">{formatCurrency(value)}</p>
                    <p className="text-xs text-gray-500">
                        {row.type === 'monthly' ? '/ mois' : '/ an'}
                    </p>
                </div>
            ),
        },
        {
            key: 'status',
            label: 'Statut',
            render: (value) => <Badge status={value} dot />,
        },
        {
            key: 'start_date',
            label: 'Début',
            sortable: true,
            render: (value) => formatDate(value),
        },
        {
            key: 'next_billing',
            label: 'Prochaine fact.',
            render: (value) => (value ? formatDate(value) : <span className="text-gray-400">-</span>),
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_, row) => (
                <div className="flex items-center gap-2">
                    <Link to={`/subscriptions/${row.id}`}>
                        <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                            <EyeIcon className="w-4 h-4 text-gray-600" />
                        </button>
                    </Link>
                    {row.status === 'active' && (
                        <>
                            <button
                                onClick={() => {
                                    setSelectedSubscription(row);
                                    setShowSuspendModal(true);
                                }}
                                className="p-2 hover:bg-orange-50 rounded-lg transition-colors"
                            >
                                <PauseCircleIcon className="w-4 h-4 text-orange-600" />
                            </button>
                            <button
                                onClick={() => {
                                    setSelectedSubscription(row);
                                    setShowCancelModal(true);
                                }}
                                className="p-2 hover:bg-danger/10 rounded-lg transition-colors"
                            >
                                <XCircleIcon className="w-4 h-4 text-danger" />
                            </button>
                        </>
                    )}
                </div>
            ),
        },
    ];

    if (loading && subscriptions.length === 0) {
        return <Loader fullScreen text="Chargement des abonnements..." />;
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-gray-900">Abonnements</h1>
                <p className="text-gray-600 mt-2">
                    Gérer les abonnements Stripe de la plateforme Zitelou
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card padding="sm" hover>
                    <div className="text-center">
                        <p className="text-sm text-gray-600">Total</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
                    </div>
                </Card>
                <Card padding="sm" hover className="border-l-4 border-success">
                    <div className="text-center">
                        <p className="text-sm text-gray-600">Actifs</p>
                        <p className="text-3xl font-bold text-success mt-1">{stats.active}</p>
                    </div>
                </Card>
                <Card padding="sm" hover className="border-l-4 border-orange-500">
                    <div className="text-center">
                        <p className="text-sm text-gray-600">Suspendus</p>
                        <p className="text-3xl font-bold text-orange-600 mt-1">{stats.suspended}</p>
                    </div>
                </Card>
                <Card padding="sm" hover className="border-l-4 border-danger">
                    <div className="text-center">
                        <p className="text-sm text-gray-600">Résiliés</p>
                        <p className="text-3xl font-bold text-danger mt-1">{stats.cancelled}</p>
                    </div>
                </Card>
            </div>

            {/* Filters Card */}
            <Card>
                <div className="space-y-4">
                    {/* Search & Filters Toggle */}
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <Input
                                placeholder="Rechercher par nom ou ID Stripe..."
                                value={searchTerm}
                                onChange={(e) => handleSearchChange(e.target.value)}
                                leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                                rightIcon={
                                    searchTerm && (
                                        <button onClick={() => handleSearchChange('')}>
                                            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                                        </button>
                                    )
                                }
                            />
                        </div>
                        <Button
                            variant={showFilters ? 'primary' : 'outline'}
                            onClick={() => setShowFilters(!showFilters)}
                            leftIcon={<FunnelIcon className="w-5 h-5" />}
                        >
                            Filtres
                            {hasActiveFilters > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
                  {hasActiveFilters}
                </span>
                            )}
                        </Button>
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                            <Select
                                label="Statut"
                                value={statusFilter}
                                onChange={(e) => handleStatusFilterChange(e.target.value)}
                                options={[
                                    { value: 'all', label: 'Tous' },
                                    { value: 'active', label: 'Actif' },
                                    { value: 'suspended', label: 'Suspendu' },
                                    { value: 'cancelled', label: 'Résilié' },
                                    { value: 'past_due', label: 'Impayé' },
                                    { value: 'expired', label: 'Expiré' },
                                ]}
                            />

                            <Select
                                label="Type"
                                value={typeFilter}
                                onChange={(e) => handleTypeFilterChange(e.target.value)}
                                options={[
                                    { value: 'all', label: 'Tous' },
                                    { value: 'monthly', label: 'Mensuel' },
                                    { value: 'yearly', label: 'Annuel' },
                                ]}
                            />

                            {hasActiveFilters > 0 && (
                                <div className="md:col-span-2">
                                    <Button variant="outline" size="sm" onClick={resetFilters}>
                                        Réinitialiser les filtres
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            {/* Results Info */}
            <div className="flex items-center justify-between text-sm text-gray-600">
                <p>
                    Affichage de {startIndex} à {endIndex} sur {pagination.total} abonnements
                </p>
                <Select
                    value={pagination.pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    options={[
                        { value: 10, label: '10 par page' },
                        { value: 25, label: '25 par page' },
                        { value: 50, label: '50 par page' },
                        { value: 100, label: '100 par page' },
                    ]}
                />
            </div>

            {/* Table */}
            <Card padding="none">
                <Table
                    columns={columns}
                    data={subscriptions}
                    keyField="id"
                    loading={loading}
                    emptyMessage="Aucun abonnement trouvé"
                />
            </Card>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
                <div className="flex justify-center">
                    <Pagination
                        currentPage={pagination.currentPage}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            )}

            {/* Suspend Confirmation Modal */}
            <ConfirmModal
                isOpen={showSuspendModal}
                onClose={() => setShowSuspendModal(false)}
                onConfirm={handleSuspend}
                title="Suspendre l'abonnement"
                message={`Êtes-vous sûr de vouloir suspendre l'abonnement de ${selectedSubscription?.user_name} ? Le parent ne pourra plus accéder aux fonctionnalités premium.`}
                confirmText="Suspendre"
                confirmVariant="warning"
            />

            {/* Cancel Confirmation Modal */}
            <ConfirmModal
                isOpen={showCancelModal}
                onClose={() => setShowCancelModal(false)}
                onConfirm={handleCancel}
                title="Résilier l'abonnement"
                message={`Êtes-vous sûr de vouloir résilier l'abonnement de ${selectedSubscription?.user_name} ? Cette action est irréversible et annulera tous les paiements futurs.`}
                confirmText="Résilier"
                confirmVariant="danger"
            />
        </div>
    );
};

export default Subscriptions;
